import React from 'react';
import { CircleCheckBig } from 'lucide-react';
import QRCode from 'react-qr-code';
import type { InvoiceHeader, InvoiceDetails, OtherCharges } from '../services/invoiceApi';
import { getInvoiceLineDescription } from '../services/invoiceApi';
import { Branch } from '../services/branchApi';
import { Customer } from '../services/customerApi';
import { formatDate } from '../utils/dateUtils';
import { getInvoiceSupplyTypeCode, getRegularSupplyNote } from '../config/uiConfig';
import { InvoiceEwayBillLayout } from './InvoiceEwayBillLayout';
import { InvoiceAddressAndMeta } from './InvoiceTaxInvoiceLayout';
import { lineAmountFromQtyRate, roundRate2, formatIndianAmount } from '../utils/taxUtils';

function amountInWords(num: number): string {
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };
  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';
  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);
  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

export function chunkInvoiceLineItems(
  details: InvoiceDetails[],
  firstPageRows: number,
  continuationRows: number
): InvoiceDetails[][] {
  if (details.length === 0) return [[]];
  if (details.length <= firstPageRows) return [details];
  const chunks: InvoiceDetails[][] = [details.slice(0, firstPageRows)];
  let rest = details.slice(firstPageRows);
  while (rest.length > 0) {
    chunks.push(rest.slice(0, continuationRows));
    rest = rest.slice(continuationRows);
  }
  return chunks;
}

/** Body rows per sheet; continuation rows slightly lower when statutory block repeats every page. */
export const CHUNK_FIRST_PAGE_ROWS = 8;
export const CHUNK_CONTINUATION_ROWS = 6;

interface InvoiceChunkedPrintProps {
  invoice: InvoiceHeader;
  branchDetails: Branch | null;
  /** Ship-from branch when different from invoice branch (e-way Dispatch From) */
  shipFromBranch?: Branch | null;
  shipToCustomer: Customer | null;
  billToCustomer: Customer | null;
  details: InvoiceDetails[];
  otherChargesList: OtherCharges[];
  calc: {
    taxableAmount: number;
    totalCgst: number;
    totalSgst: number;
    totalIgst: number;
    totalTax: number;
    totalQty: number;
    totalAmount: number;
  };
  hsnWiseTaxDetails: HsnWiseRow[];
  isGst: boolean;
  qrValue: string;
  copies: number;
  showEwayBillOnView: boolean;
  /** Resolved signer display name for e-invoice digital signature block */
  digitallySignedByName?: string;
  /** Formatted signedOn — shown as "Digitally signed on …" under signer name */
  digitallySignedOn?: string;
  /** Effective branch LUT line for export invoices (from GET …/branch-lut/effective) */
  exportBranchLutDisplay?: string;
  exportBranchLutLoading?: boolean;
}

/**
 * E-invoice document: Header (everything above line items, including IRN/QR) on every page; body =
 * line items split across pages; footer (amount in words + HSN + declaration + doc footer) only on
 * the last body page of each copy. Totals rows in the line table appear only on the last body page.
 */
export const InvoiceChunkedPrint: React.FC<InvoiceChunkedPrintProps> = ({
  invoice,
  branchDetails,
  shipFromBranch = null,
  shipToCustomer,
  billToCustomer,
  details,
  otherChargesList,
  calc,
  hsnWiseTaxDetails,
  isGst,
  qrValue,
  copies,
  showEwayBillOnView,
  digitallySignedByName,
  digitallySignedOn,
  exportBranchLutDisplay = '',
  exportBranchLutLoading = false,
}) => {
  const branchDisplayName = branchDetails?.branchName ?? invoice.branchName ?? '';
  const supplyNote = getRegularSupplyNote(getInvoiceSupplyTypeCode(invoice));
  const chunks = chunkInvoiceLineItems(details, CHUNK_FIRST_PAGE_ROWS, CHUNK_CONTINUATION_ROWS);
  const invoicePagesPerCopy = Math.max(1, chunks.length);
  const totalBodyPages = copies * invoicePagesPerCopy;
  /** "Page x of y" on the Tax Invoice is only for e-invoice body sheets (excludes e-way annex). */
  const einvoicePageTotal = totalBodyPages;
  // Hide "(Page x)" in the title for e-invoice prints (matches mockup / requested behavior)
  const showPageNumberInTitle = false;

  const renderAddressAndMeta = () => (
    <InvoiceAddressAndMeta
      invoice={invoice}
      branchDetails={branchDetails}
      shipToCustomer={shipToCustomer}
      billToCustomer={billToCustomer}
      exportBranchLutDisplay={exportBranchLutDisplay}
      exportBranchLutLoading={exportBranchLutLoading}
    />
  );

  const renderLineTableHead = () => (
    <thead>
      <tr className="bg-gray-100">
        <th className="w-[2rem]">#</th>
        <th className="w-[30%] min-w-0 text-left">Description of Goods</th>
        <th className="w-[3rem]">Boxes</th>
        <th className="w-[4.5rem] max-w-[4.5rem]">Heat No/Container</th>
        <th className="w-[3.5rem]">HSN/SAC</th>
        <th className="w-[2.75rem]">Qty</th>
        <th className="w-[2.75rem]">Rate</th>
        <th className="w-[2.25rem]">Per</th>
        <th className="w-[3.25rem] invoice-amount-col">Amt</th>
      </tr>
    </thead>
  );

  /** Jurisdiction + e-invoice page x of y (body pages only; e-way sheet is not counted). */
  const renderPrintFooter = () => (
    <div className="invoice-print-doc-footer mt-4 pt-2 border-t border-gray-800 text-center space-y-0.5">
      <p className="font-semibold">SUBJECT TO JAIPUR JURISDICTION</p>
      <p>This is a Computer Generated Invoice</p>
    </div>
  );
  const renderTotalsAndAfter = () => (
    <>
      <div className="mt-3 border-t border-gray-800 pt-3">
        <p className="invoice-footer-text">
          <strong>Amount Chargeable (in words):</strong> INR {amountInWords(calc.totalAmount)}
        </p>
      </div>
      <div className="mt-3 border border-gray-800 rounded overflow-hidden">
        <table className="invoice-summary-table">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-center font-semibold">HSN/SAC</th>
              <th className="text-center font-semibold">Taxable Value</th>
              {!isGst && (
                <>
                  <th className="text-center font-semibold">CGST Rate</th>
                  <th className="text-center font-semibold">CGST Amt</th>
                  <th className="text-center font-semibold">SGST Rate</th>
                  <th className="text-center font-semibold">SGST Amt</th>
                </>
              )}
              {isGst && (
                <>
                  <th className="text-center font-semibold">IGST Rate</th>
                  <th className="text-center font-semibold">IGST Amt</th>
                </>
              )}
              <th className="text-center font-semibold">Total Tax Amount</th>
            </tr>
          </thead>
          <tbody>
            {hsnWiseTaxDetails.map((row, idx) => (
              <tr key={idx}>
                <td>{row.hsn}</td>
                <td className="invoice-amount-col">{formatIndianAmount(row.taxableValue)}</td>
                {!isGst && (
                  <>
                    <td className="text-center">{toDisplayRate(row.cgstRate).toFixed(2)}%</td>
                    <td className="invoice-amount-col">{formatIndianAmount(row.cgstAmount)}</td>
                    <td className="text-center">{toDisplayRate(row.sgstRate).toFixed(2)}%</td>
                    <td className="invoice-amount-col">{formatIndianAmount(row.sgstAmount)}</td>
                  </>
                )}
                {isGst && (
                  <>
                    <td className="text-center">{toDisplayRate(row.igstRate).toFixed(2)}%</td>
                    <td className="invoice-amount-col">{formatIndianAmount(row.igstAmount)}</td>
                  </>
                )}
                <td className="invoice-amount-col">{formatIndianAmount(row.totalTaxAmount)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td>TOTAL</td>
              <td className="invoice-amount-col">{formatIndianAmount(calc.taxableAmount)}</td>
              {!isGst && (
                <>
                  <td />
                  <td className="invoice-amount-col">{formatIndianAmount(calc.totalCgst)}</td>
                  <td />
                  <td className="invoice-amount-col">{formatIndianAmount(calc.totalSgst)}</td>
                </>
              )}
              {isGst && (
                <>
                  <td />
                  <td className="invoice-amount-col">{formatIndianAmount(calc.totalIgst)}</td>
                </>
              )}
              <td className="invoice-amount-col">{formatIndianAmount(calc.totalTax)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-3 border border-gray-800 rounded px-2 py-2">
        <p className="invoice-footer-text">
          <strong>Tax Amount (in words):</strong> INR {amountInWords(calc.totalTax)}
        </p>
        <p className="invoice-footer-text font-bold mt-1.5">Company&apos;s PAN: {branchDetails?.panNo ?? 'NA'}</p>
      </div>
      <div className="mt-3 flex justify-between gap-4 border-t border-gray-800 pt-3 items-start">
        <div className="min-w-0 flex-1">
          <p className="invoice-section-heading">Declaration:</p>
          <p className="invoice-footer-text mt-0.5">
            We declare that this invoice shows the actual price of the goods described and that all particulars are true and
            correct.
          </p>
        </div>
        <div className="text-right shrink-0 min-w-[11rem]">
          {digitallySignedByName?.trim() ? (
            <div>
              {branchDisplayName ? (
                <p className="invoice-footer-text font-bold text-right mb-1.5">for {branchDisplayName}</p>
              ) : null}
              <div className="flex items-start justify-end gap-1.5">
                <CircleCheckBig className="invoice-digital-sign-tick h-6 w-6 shrink-0" aria-hidden />
                <div className="text-right invoice-footer-text leading-tight">
                  <div>{digitallySignedByName.trim()}</div>
                  {digitallySignedOn?.trim() ? (
                    <div className="mt-0.5">Digitally signed on {digitallySignedOn.trim()}</div>
                  ) : null}
                </div>
              </div>
              <p className="invoice-footer-text font-bold text-right mt-2 border-b border-black pb-0.5">
                Authorised Signatory
              </p>
            </div>
          ) : (
            <p className="invoice-footer-text font-semibold">Authorized Signatory</p>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="invoice-chunked-print-root invoice-chunked-print invoice-a4-stage">
      {Array.from({ length: copies }).map((_, copyIndex) => {
        const copyLabel =
          copyIndex === 0
            ? 'ORIGINAL FOR RECIPIENT'
            : copyIndex === 1
              ? 'DUPLICATE FOR TRANSPORTER'
              : copyIndex === 2
                ? 'TRIPLICATE FOR SUPPLIER'
                : 'EXTRA COPY';
        return (
          <React.Fragment key={copyIndex}>
            {chunks.map((chunk, chunkIndex) => {
              const isLastChunk = chunkIndex === chunks.length - 1;
              const globalStart = chunks.slice(0, chunkIndex).reduce((s, c) => s + c.length, 0);
              const currentPage = copyIndex * chunks.length + chunkIndex + 1;
              const nextPageNum = currentPage + 1;

              const breakAfter =
                !isLastChunk || copyIndex < copies - 1
                  ? 'always'
                  : showEwayBillOnView
                    ? 'always'
                    : 'auto';

              return (
                <div
                  key={`${copyIndex}-${chunkIndex}`}
                  className="invoice-a4-sheet invoice-chunk-page invoice-container invoice-doc bg-white"
                  style={{ pageBreakAfter: breakAfter as React.CSSProperties['pageBreakAfter'] }}
                >
                  {/* HEADER: full document header on every page (above line items) */}
                  <div className="invoice-doc-header invoice-print-header-block print:break-inside-avoid">
                    <div className="invoice-doc-title-row">
                      <h2 className="invoice-doc-title">
                        Tax Invoice
                        {showPageNumberInTitle && (
                          <span className="font-bold"> (Page {currentPage})</span>
                        )}
                      </h2>
                      <div className="invoice-doc-copy-label">({copyLabel})</div>
                    </div>

                    <div className="invoice-einvoice-strip mb-3">
                      <div className="invoice-einvoice-inner">
                        <div className="invoice-einvoice-left">
                          {supplyNote ? (
                            <p className="invoice-footer-text font-medium self-start">{supplyNote}</p>
                          ) : (
                            <div />
                          )}
                          <table className="invoice-irn-table">
                            <tbody>
                              <tr>
                                <td className="invoice-irn-label">IRN</td>
                                <td className="invoice-irn-value break-all">{invoice.irn || '—'}</td>
                              </tr>
                              <tr>
                                <td className="invoice-irn-label">Ack No.</td>
                                <td className="invoice-irn-value">{invoice.ackNo || '—'}</td>
                              </tr>
                              <tr>
                                <td className="invoice-irn-label">Ack Date</td>
                                <td className="invoice-irn-value">
                                  {invoice.ackDate ? formatDate(invoice.ackDate) : '—'}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="invoice-einvoice-right">
                          <div className="invoice-section-heading">e-Invoice</div>
                          {qrValue ? (
                            <div
                              className="bg-white border border-black p-1"
                              style={{ width: '2in', height: '2in' }}
                            >
                              <QRCode value={qrValue} style={{ width: '100%', height: '100%' }} />
                            </div>
                          ) : (
                            <div className="invoice-party-line">QR not available</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {renderAddressAndMeta()}
                  </div>

                  {/* BODY: line items only (+ continuation note); summary rows in table only on last body page */}
                  <div className="invoice-doc-body overflow-x-auto invoice-print-line-items">
                    <table className="invoice-line-table">
                      {renderLineTableHead()}
                      <tbody>
                        {chunk.map((item, i) => (
                          <tr key={item.id}>
                            <td>{globalStart + i + 1}</td>
                            <td className="text-left break-words w-[30%] min-w-0">{getInvoiceLineDescription(item)}</td>
                            <td>{item.boxes ?? item.packingDetailIds?.length ?? '—'}</td>
                            <td className="break-all">{item.userProvidedHeatNo || item.heatNo || '—'}</td>
                            <td className="break-all">{item.hsn ?? '—'}</td>
                            <td>{item.qty}</td>
                            <td>{roundRate2(item.rate ?? 0).toFixed(2)}</td>
                            <td>{item.uom ?? 'Nos'}</td>
                            <td className="invoice-amount-col">{formatIndianAmount(lineAmountFromQtyRate(item.qty ?? 0, item.rate ?? 0))}</td>
                          </tr>
                        ))}
                        {!isLastChunk && (
                          <tr className="bg-amber-50">
                            <td colSpan={9} className="text-center font-semibold italic">
                              continued to page number {nextPageNum}
                            </td>
                          </tr>
                        )}
                        {isLastChunk && (
                          <>
                            <tr>
                              <td colSpan={5} className="text-right font-semibold" />
                              <td />
                              <td colSpan={2} className="text-right font-semibold" />
                              <td className="invoice-amount-col font-semibold">{formatIndianAmount(calc.taxableAmount)}</td>
                            </tr>
                            {!isGst && (
                              <>
                                <tr>
                                  <td colSpan={4} className="text-right font-semibold">
                                    CGST
                                  </td>
                                  <td colSpan={4} className="text-right font-semibold" />
                                  <td className="invoice-amount-col font-semibold">{formatIndianAmount(calc.totalCgst)}</td>
                                </tr>
                                <tr>
                                  <td colSpan={4} className="text-right font-semibold">
                                    SGST
                                  </td>
                                  <td colSpan={4} className="text-right font-semibold" />
                                  <td className="invoice-amount-col font-semibold">{formatIndianAmount(calc.totalSgst)}</td>
                                </tr>
                              </>
                            )}
                            {isGst && (
                              <tr>
                                <td colSpan={4} className="text-right font-semibold">
                                  IGST
                                </td>
                                <td colSpan={4} className="text-right font-semibold" />
                                <td className="invoice-amount-col font-semibold">{formatIndianAmount(calc.totalIgst)}</td>
                              </tr>
                            )}
                            {otherChargesList.map((oc) => (
                              <tr key={oc.id}>
                                <td colSpan={4} className="text-right font-semibold">
                                  {oc.chargeName ?? oc.chargeMaster?.chargeName ?? ''} (
                                  {oc.chargeDescription ?? oc.chargeMaster?.description ?? ''})
                                </td>
                                <td colSpan={4} />
                                <td className="invoice-amount-col font-semibold">{formatIndianAmount(oc.amount ?? 0)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100 font-semibold">
                              <td colSpan={4} className="text-right py-1">
                                TOTAL
                              </td>
                              <td />
                              <td className="text-center">{calc.totalQty}</td>
                              <td colSpan={2} />
                              <td className="invoice-amount-col">₹ {formatIndianAmount(calc.totalAmount)}</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* FOOTER: everything below line items — only on last body page of this copy */}
                  {isLastChunk && (
                    <div className="invoice-doc-footer">
                      {renderTotalsAndAfter()}
                      {renderPrintFooter()}
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}

      {showEwayBillOnView && (
        <div
          className="invoice-a4-sheet invoice-eway-append print:break-before-page"
          style={{ pageBreakBefore: 'always' }}
        >
          <InvoiceEwayBillLayout
            invoice={invoice}
            branchDetails={branchDetails}
            shipFromBranch={shipFromBranch}
            shipToCustomer={shipToCustomer}
          />
        </div>
      )}
    </div>
  );
};
